const ACCOUNT_DELETED_COPY_KEY = "fawkes.account-deleted-copy";

export interface AccountDeletedCopy {
	title: string;
	body: string;
}

/** Keep the signed-in translation after the pre-login bundle is restored. */
export function rememberAccountDeletedCopy(copy: AccountDeletedCopy): void {
	if (typeof window === "undefined" || !copy.title || !copy.body) return;
	sessionStorage.setItem(ACCOUNT_DELETED_COPY_KEY, JSON.stringify(copy));
}

export function readAccountDeletedCopy(): AccountDeletedCopy | null {
	if (typeof window === "undefined") return null;
	const raw = sessionStorage.getItem(ACCOUNT_DELETED_COPY_KEY);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<AccountDeletedCopy>;
		if (!parsed.title || !parsed.body) return null;
		return { title: parsed.title, body: parsed.body };
	} catch {
		return null;
	}
}

export function clearAccountDeletedCopy(): void {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(ACCOUNT_DELETED_COPY_KEY);
}

/**
 * 401 and 404 resolve as "ended" so the global mutation toast does not fire.
 */
export async function deleteOwnAccount(
	deleteUser: () => Promise<void>,
): Promise<"deleted" | "ended"> {
	try {
		await deleteUser();
		return "deleted";
	} catch (error) {
		if (isEndedDeleteAccountSession(deleteAccountStatus(error))) {
			return "ended";
		}
		throw error;
	}
}

export function showsEntraDeleteCopy(
	usingFirstPartyAuth: boolean | undefined,
): boolean {
	return usingFirstPartyAuth === false;
}

export function deleteAccountStatus(error: unknown): number | undefined {
	if (!error || typeof error !== "object" || !("status" in error)) {
		return undefined;
	}
	const status = (error as { status?: unknown }).status;
	return typeof status === "number" ? status : undefined;
}

/** 401 and 404 mean the account is already gone or the session was revoked. */
export function isEndedDeleteAccountSession(status: number | undefined): boolean {
	return status === 401 || status === 404;
}

export type DeleteAccountExit = "account-deleted" | "access-removed";

/**
 * Leave the signed-in app after account deletion.
 * Clears the local session only. Does not call the log-out API.
 * Success opens the account-deleted screen. That screen redirects to sign-in.
 */
export async function leaveAfterAccountDeletion(input: {
	kind: "success" | "access-removed";
	clearLocalSession: () => void;
	navigate: (exit: DeleteAccountExit) => Promise<void> | void;
	setLoading: (loading: boolean) => void;
}): Promise<void> {
	input.clearLocalSession();
	await input.navigate(
		input.kind === "success" ? "account-deleted" : "access-removed",
	);
	input.setLoading(false);
}
